// ** React Imports
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Modal from "@mui/material/Modal";

// ** MUI Imports
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import InputMask from "react-input-mask";
// ** MUI Icons
import SendIcon from "@mui/icons-material/Send";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageIcon from "@mui/icons-material/Message";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

// ** SweetAlert
import Swal from "sweetalert2";
import { CloseOutlined } from "@mui/icons-material";

const Setting = () => {
  const rights = useSelector((state) => state.login.rights);
  const [loading, setLoading] = useState(false);
  const [reminderData, setReminderData] = useState({
    to: "",
    message: "",
  });
  const token = localStorage.getItem("Token");
  const auth = `Bearer ${token}`;
  const headers = {
    Authorization: auth,
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReminderData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const [refresh, setRefresh] = useState(false);

  const [openModal, setOpenModal] = React.useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
    setReminderData({
      to: "",
      message: "",
    });
  };
  const handleOpen = () => {
    setOpenModal(true);
  };
  const validateForm = () => {
    if (!reminderData.to) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter a phone number",
        confirmButtonColor: "#d33",
        didRender: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = 1800; // Increase z-index so it appears above everything
        },
      });
      return false;
    }
    if (!reminderData.message) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter a message",
        confirmButtonColor: "#d33",
        didRender: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = 1800; // Increase z-index so it appears above everything
        },
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    const cleanPhone = reminderData.to.replace(/[()\-\s]/g, "");

    const payload = {
      ...reminderData,
      to: cleanPhone,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/TwilioSMS/sendReminder`,
        payload,
        { headers },
      );

      // Check success from API response
      if (response && response.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Reminder sent successfully",
          confirmButtonColor: "#3085d6",
          timer: 2000,
          showConfirmButton: false,
          didRender: () => {
            const el = document.querySelector(".swal2-container");
            if (el) el.style.zIndex = 1800; // Increase z-index so it appears above everything
          },
        });
        setReminderData({ to: "", message: "" });
        setRefresh(!refresh);
        setOpenModal(false);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: (response && response.message) || "Failed to send reminder",
          confirmButtonColor: "#d33",
          didRender: () => {
            const el = document.querySelector(".swal2-container");
            if (el) el.style.zIndex = 1800; // Increase z-index so it appears above everything
          },
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "Failed to send reminder",
        confirmButtonColor: "#d33",
        didRender: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = 1800; // Increase z-index so it appears above everything
        },
      });
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.1,
    },

    {
      field: "to",
      headerName: "To",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.3,
    },
    {
      field: "message",
      headerName: "Message",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.3,
    },
    {
      field: "createdBy",
      headerName: "Sent By",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.3,
    },
  ];
  const gridRowStyle = {
    boxShadow: 2,
    border: 2,
    borderRadius: 2,
    background: "drak",
    fontSize: "13.4px !important",
    color: "drak !important",
    borderColor: "#326C1D",
    "& .MuiDataGrid-cell:hover": {
      color: "rgba(5, 152, 236, 0.637) !important",
      fontSize: "14.6px !important",
    },
    "& .super-app-theme--header": {
      backgroundColor: "#377562",
      borderRadius: 0.1,
      color: "white",
      fontSize: "16px !important",
    },
  };
  const gridClasses = {
    columnHeader: "MuiDataGrid-columnHeaders",
  };
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/TwilioSMS/GetReminders`, {
        headers,
      })
      .then((response) => {
        setRows(response.data);
        setLoading(false);
      })
      .catch((error) => {});
  }, [refresh]);

  return (
    <div className="page-content">
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: 500,
            borderRadius: 3,
            boxShadow: 5,
            position: "relative",
            p: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#377562",
              color: "#fff",
              px: 3,
              py: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff" }}>
              Appointment Reminders
            </Typography>
            <Box
              onClick={handleCloseModal}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 20,
                lineHeight: 1,
              }}
            >
              <CloseOutlined />
            </Box>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4, pt: 3 }}>
            {/* Phone Number Input */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#666",
                }}
              >
                <PhoneIcon sx={{ fontSize: 18, color: "#1976d2" }} />
                Phone Number
              </Typography>
              <InputMask
                mask="+1 (999) 999-9999"
                value={reminderData.to}
                onChange={handleInputChange}
                disabled={loading}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    fullWidth
                    name="to"
                    placeholder="+1 (123) 456-7890"
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        bgcolor: "#fff",
                      },
                    }}
                  />
                )}
              </InputMask>
            </Box>

            {/* Message Input */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#666",
                }}
              >
                <MessageIcon sx={{ fontSize: 18, color: "#1976d2" }} />
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="message"
                value={reminderData.message}
                onChange={handleInputChange}
                placeholder="Type your reminder message here..."
                variant="outlined"
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                  },
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={<SendIcon />}
              sx={{
                borderRadius: 1.5,
                py: 1.5,
                bgcolor: "#377562",
                "&:hover": {
                  bgcolor: "#326C1D",
                },
              }}
            >
              {loading ? "Sending..." : "Send Reminder"}
            </Button>
          </CardContent>
        </Card>
      </Modal>
      <Button
        sx={{ mb: 1 }}
        className="Button"
        onClick={handleOpen}
        variant="contained"
      >
        <AddOutlinedIcon
          fontSize="small"
          style={{
            color: "white",
            paddingRight: "4px",
            paddingBottom: "2px",
          }}
        ></AddOutlinedIcon>
        Add New
      </Button>
      <Box marginTop={2}>
        {loading ? (
          <div style={{ margin: "5rem", textAlign: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ height: 400, width: "100%" }}>
            {rows === null || rows.length === 0 ? (
              <Typography>No Record found</Typography>
            ) : (
              <DataGrid
                rows={rows}
                classes={gridClasses}
                columns={columns}
                fullWidth
                pagination
                pageSizeOptions={[25]}
                sx={gridRowStyle}
                rowHeight={30}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                }}
              />
            )}
          </div>
        )}
      </Box>
    </div>
  );
};

export default Setting;
